import {
  afterNextRender,
  ChangeDetectorRef,
  computed,
  inject,
  Injector,
  signal,
  Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, first, switchMap } from 'rxjs/operators';
import { AfmOptionResolverService } from './afm-resovler.service';

import { Pipe, PipeTransform } from '@angular/core';

export interface RadioOption {
  option_key: string | number;
  disabled?: boolean;
}

@Pipe({
  name: 'isOptionDisabled',
  pure: true
})
export class IsOptionDisabledPipe implements PipeTransform {
  transform(
    option: RadioOption,
    activeOptions: RadioOption[] | null | undefined,
  ): boolean {

    const list = activeOptions ?? [];
    const isPresentAndActive = list.some(
      (opt) => opt.option_key === option.option_key && !opt.disabled
    );

    return !isPresentAndActive;
  }
}

@Pipe({
  name: 'isGridOptionDisabled',
  pure: true,
  standalone: true,
})
export class IsGridOptionDisabledPipe implements PipeTransform {

  transform(
    option: RadioOption,
    activeOptions: RadioOption[] | null | undefined,
  ): boolean {

    const list = activeOptions ?? [];

    return !list.some(
      opt =>
        String(opt.option_key) === String(option.option_key) &&
        !opt.disabled
    );
  }

}

export interface AfmOptionsResolverReturn<TOption = any> {
  options: Signal<TOption[]>;
  hydrationCompleted: Signal<boolean>;
  registerHydrate: (hydrateFn: () => void) => void;
}

export function createAfmOptionsResolver<TOption = any, TPageData = any>(
  getOptions: () => TOption[] | null | undefined,
  getPageData: () => TPageData,
): AfmOptionsResolverReturn<TOption> {
  const afmResolver = inject(AfmOptionResolverService);
  const cdr = inject(ChangeDetectorRef);
  const injector = inject(Injector);

  // Hydration state tracking
  const _hydrationCompleted = signal<boolean>(false);

  // 1. Wrap in computed() for valid Signal input
  const optionsSignal = computed(() => ({
    options: getOptions(),
    pageData: getPageData(),
  }));

  // 2. Convert to Observable
  const options$ = toObservable(optionsSignal).pipe(
    switchMap(({ options, pageData }) =>
      afmResolver.resolveOptions(options, pageData),
    ),
  );

  // 3. Convert back to Signal for template usage
  const options = toSignal(options$, { initialValue: [] as TOption[] });

  // 4. Hydration registration with DOM render waiting & completion tracking
  const registerHydrate = (hydrateFn: () => void) => {
    options$
      .pipe(
        filter((opts) => !!opts && opts.length > 0),
        first(),
      )
      .subscribe((e) => {
        // Force Angular to run change detection for the updated options
        cdr.detectChanges();

        // Wait for Angular to flush DOM rendering updates to the screen
        afterNextRender(
          () => {
            setTimeout(() => {
              try {
                hydrateFn();
              } finally {
                // Set flag to true once hydration executes (even if it throws)
                _hydrationCompleted.set(true);
                cdr.markForCheck();
              }
            }, 100);
          },
          { injector },
        );
      });
  };

  return {
    options,
    hydrationCompleted: _hydrationCompleted.asReadonly(),
    registerHydrate,
  };
}